export type MultiActionProperty = {
	name: string;
	value: unknown;
};

export type MultiActionFilter = {
	field: string;
	comparison: string;
	value: unknown;
};

export type MultiActionStatus = 'pending' | 'cancelled' | 'executing' | 'completed' | 'failed';

export type MultiActionConfig = {
	id: string;
	task: 'Update' | 'Create' | 'Delete';
	entity: string;
	action: string;
	entityRoute: string;
	filters: MultiActionFilter[];
	properties: MultiActionProperty[];
	selectedIds: number[];
	estimatedCount: number;
	description: string;
	status: MultiActionStatus;
	resultMessage?: string;
	/** Set when the proposal is backed by an agent_approval_request row.
	 *  WEB-6457: chat proposals persist across reload via this id. */
	approvalId?: number | null;
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

export type WriteProposal = {
	entity: string;
	action: 'create' | 'update' | 'delete';
	data: Record<string, unknown>;
	status: 'pending' | 'saving' | 'saved' | 'cancelled' | 'error';
	/** Estimated audience size for segment proposals, computed at draft time
	 *  so the preview card can show it before the user confirms (WEB-7261). */
	matchingContacts?: number;
	result?: Record<string, unknown>;
	error?: string;
	approvalId?: number | null;
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

export type WriteProposalGroup = {
	items: WriteProposal[];
};

export type EmailDraft = {
	contactIds: number[];
	subject: string;
	body: string;
	ccContactIds?: number[];
	clientId?: number;
	/** Files attached to the email, resolved to id + filename for display.
	 *  Agent-chosen and not user-editable. */
	attachments?: { id: number; name: string }[];
	status: 'pending' | 'sending' | 'sent' | 'cancelled' | 'error';
	error?: string;
	/** Set when the proposal is backed by an agent_approval_request row.
	 *  WEB-6572: chat email drafts persist across reload via this id. */
	approvalId?: number | null;
	/** Mail ids returned by the send executor; populated when the proposal
	 *  resolves to status='sent'. Used to surface an "Open" link to the
	 *  sent mail drawer. */
	mailIds?: number[];
};

export type OfferDraftLineItem = {
	productId: number;
	/** Catalogue name, display-only, so the rep can verify the agent's match. */
	productName?: string;
	quantity: number;
	unitPrice?: number;
	discount?: number;
};

export type OfferDraft = {
	clientId: number;
	contactId: number;
	products: OfferDraftLineItem[];
	currency: string;
	validUntil: string;
	description?: string;
	notes?: string;
	status: 'pending' | 'saving' | 'saved' | 'cancelled' | 'error';
	error?: string;
	/** Set when the draft is backed by an agent_approval_request row (persists across reload). */
	approvalId?: number | null;
	/** Opportunity (order) created when the offer is approved. */
	resultEntityType?: string | null;
	resultEntityId?: number | null;
};

export type MessageAttachment = {
	name: string;
	mimeType: string;
	sizeBytes?: number;
	url?: string;
};

export type ChartRow = { key: string; value: number };

export const CHART_TYPES = ['bar', 'line', 'area', 'donut', 'bigNumber', 'table'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

export type ChartSpec = {
	chartType: ChartType;
	title: string;
	rows: ChartRow[];
	xLabel?: string;
	yLabel?: string;
	currency?: string;
};

// A single item within an approvals review group. Today the agent can produce
// CRM-write approvals AND send-email approvals in the same turn (post-meeting
// agent does both), so the group renders either kind in a unified list with
// one header + one bulk-action footer.
export type ApprovalGroupItem = { kind: 'write'; proposal: WriteProposal } | { kind: 'email'; draft: EmailDraft };

export type ApprovalsGroup = { items: ApprovalGroupItem[] };

export type AssistantMessage = {
	id?: number;
	role: 'user' | 'assistant';
	content: string;
	regDate?: string;
	images?: string[];
	attachments?: MessageAttachment[];
	metadata?: Record<string, unknown>;
	multiAction?: MultiActionConfig;
	writeProposal?: WriteProposal;
	// Mixed approvals review group. Held under the legacy `writeProposalGroup`
	// name to keep persisted-message and live-stream code paths talking the
	// same shape; the renamed item type makes the union explicit at use sites.
	writeProposalGroup?: ApprovalsGroup;
	emailDraft?: EmailDraft;
	offerDraft?: OfferDraft;
	chart?: ChartSpec;
	thinking?: string;
	suggestedReplies?: string[];
};

export type Conversation = {
	id: number;
	title: string;
	contextType: string;
	contextId?: number;
	/** Set when this is a delegated child conversation — the delegating conversation. */
	parentConversationId?: number | null;
	regDate: string;
	modDate: string;
	starred?: boolean;
};
